import { RadioSegments } from '@/components/ui/SegmentedControl'
import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

export interface PeriodOption<T extends string> {
  value: T
  /** Short by design — "1D", "3M", "All" — because every tab is one fixed 48px box. */
  label: string
}

interface PeriodTabsProps<T extends string> {
  /**
   * The periods this chart can draw, in order. The caller's list, never a fixed one: a period
   * with no series behind it is left out rather than drawn as a promise the chart cannot keep.
   */
  options: readonly PeriodOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  /** `boxed` sets the row in a track, as Home's NEPSE today does — see `SKINS`. */
  variant?: 'plain' | 'boxed'
  className?: string | undefined
}

/**
 * Home's NEPSE today boxes the row, as the mock does (2026-10-28): one bordered track, the inset
 * fill under a `border-border-subtle` hairline, and the selected period lifted onto it as
 * SegmentedControl's thumb (`bg-popover`, `border-border-strong`), the rest bare muted words. The
 * tabs close up to touch at the mock's 28px, the 48px box each. The mock pads the track 2px; the
 * 4-grid makes that 4, so the track stands 38 to the mock's 34.
 */
const SKINS = {
  plain: { row: 'flex items-center gap-2', thumb: 'border-border bg-popover', tab: 'h-8' },
  boxed: {
    row: 'inline-flex items-center border border-border-subtle bg-muted p-1',
    thumb: 'border-border-strong bg-popover',
    tab: 'h-7',
  },
} as const

/**
 * A chart's period row — 1D 1W 1M 3M 1Y All — the compact `tf/*` tabs (`4236:69879`, `6039:2`).
 *
 * Each tab is a 48px box (Figma 47–52), 32px tall (Figma 30) with 8px between (Figma 6), all on
 * the 4-grid. The label is 14, the reference's period size (Figma drew 12). Unselected periods
 * are bare muted words; the selected one sits on a `bg-popover` thumb with a `border-border`
 * hairline and a 600 label, and the thumb slides between periods. The row never stretches across
 * its container: Stock Overview's 135px-wide tabs (`4236:73517`) are the frame out of step with the
 * other twenty.
 *
 * 48px gives way at both ends: `min-w-fit` lets a label longer than the box widen it instead of
 * spilling out, and lets a tab shrink only as far as its own label. That shrink is what a card on
 * a 360px phone needs, since six tabs make a 328px row and the card has about 304px inside it.
 *
 * A radio group, like SegmentedControl, whose engine it shares: a period is a value the chart is
 * drawn from, not a panel.
 *
 * 32px tall, so below `lg` the target grows to 44px (`TOUCH_TARGET_Y`). Only vertically: the
 * tabs are 8px apart, and growing sideways would put one tab's target over its neighbour's.
 */
export default function PeriodTabs<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'plain',
  className,
}: PeriodTabsProps<T>) {
  const skin = SKINS[variant]
  return (
    <RadioSegments
      options={options}
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      className={cn(skin.row, className)}
      thumbClassName={skin.thumb}
      segmentClassName={(checked) =>
        cn(
          'relative inline-flex',
          skin.tab,
          'w-12 min-w-fit items-center justify-center border border-transparent px-2 text-body-m whitespace-nowrap transition-colors',
          TOUCH_TARGET_Y,
          checked
            ? 'text-h-s text-foreground'
            : 'text-muted-foreground hover:text-foreground focus-visible:text-foreground',
        )
      }
    />
  )
}
